import React, {useState, useContext, useEffect} from "react";
import {
    Box, Stack, Typography, IconButton, Collapse,
    CircularProgress, Chip, Table, TableHead,
    TableRow, TableCell, TableBody, Tooltip, Button
} from "@mui/material";
import {IconRefresh, IconChevronRight, IconServer, IconInfoCircle} from "@tabler/icons-react";
import ToolModal from "./MCPToolModal";
import {Context} from "../../../context/AppContext";

export const MCPDiscoveryPage = ({project}) => {
    const {state, getMCPServers} = useContext(Context);
    const {mcpServers: servers, mcpLoading: loading} = state;

    const [selectedServer, setSelectedServer] = useState(null);
    const [selectedTool, setSelectedTool] = useState(null);

    const [expandedPrompts, setExpandedPrompts] = useState({});

    const togglePrompt = (name) => {
        setExpandedPrompts(prev => ({...prev, [name]: !prev[name]}));
    };
    useEffect(() => {
        getMCPServers(project.id);
    }, [project.id]);

    const totalTools = servers.reduce((acc, s) => acc + (s.tools.tools?.length || 0), 0);

    return (
        <Box sx={{height: "100%", width: "90%", mx: "auto", display: "flex", flexDirection: "column", gap: 2, p: 2}}>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={600}>MCP Servers Discovery</Typography>
                <IconButton size="small" color="secondary" onClick={() => getMCPServers(project.id)}>
                    <IconRefresh size={18}/>
                </IconButton>
            </Stack>

            <Stack direction="row" gap={2} sx={{bgcolor: "grey.100", borderRadius: 4, p: 2}}>
                <Box sx={{textAlign: "center", px: 3}}>
                    <Typography variant="h3" fontWeight={600}>{servers.length}</Typography>
                    <Typography variant="body2">Servers</Typography>
                </Box>
                <Box sx={{textAlign: "center", px: 3}}>
                    <Typography variant="h3" fontWeight={600}>{totalTools}</Typography>
                    <Typography variant="body2">Total Tools</Typography>
                </Box>
            </Stack>

            {loading && (
                <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
                    <CircularProgress size={24}/>
                </Box>
            )}
            <Stack gap={2}>
                {!loading && servers.map((server) => {
                    const isOpen = selectedServer?.id === server.id;
                    const tools = server.tools?.tools || [];
                    const prompts = server.prompts || {};

                    return (
                        <Box key={server.id}
                             sx={{border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden"}}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                onClick={() => setSelectedServer(isOpen ? null : server)}
                                sx={{
                                    px: 2, py: 1.5, cursor: "pointer", bgcolor: "grey.50",
                                    "&:hover": {bgcolor: "grey.100"}
                                }}
                            >
                                <IconServer size={16}/>
                                <Typography fontSize={13} fontWeight={600} flex={1}>{server.name}</Typography>
                                <Tooltip title={server.active ? "Active" : "Inactive"}>
                                    <Box sx={{
                                        width: 10, height: 10, borderRadius: "50%", border: 2,
                                        borderColor: server.active ? "success.main" : "error.main",
                                        bgcolor: server.active ? "success.light" : "error.light"
                                    }}/>
                                </Tooltip>
                                <Chip
                                    label={`${tools.length} tools`}
                                    size="small"
                                    sx={{fontSize: 10, height: 18}}
                                />
                                <IconChevronRight
                                    size={16}
                                    style={{
                                        transition: "transform 0.2s",
                                        transform: isOpen ? "rotate(90deg)" : "rotate(0deg)"
                                    }}
                                />
                            </Stack>

                            <Collapse in={isOpen}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 700, fontSize: 12}}>Tool</TableCell>
                                            <TableCell sx={{fontWeight: 700, fontSize: 12}}>Description</TableCell>
                                            <TableCell sx={{fontWeight: 700, fontSize: 12}}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tools.map((tool) => (
                                            <TableRow key={tool.name} hover>
                                                <TableCell
                                                    sx={{fontSize: 12, whiteSpace: "nowrap"}}>{tool.name}</TableCell>
                                                <TableCell sx={{
                                                    fontSize: 12,
                                                    color: "text.secondary",
                                                    maxWidth: 280
                                                }}>{tool.description}</TableCell>
                                                <TableCell>
                                                    <Button
                                                        onClick={() => setSelectedTool(tool)}
                                                        size="small" variant="text"
                                                        startIcon={<IconInfoCircle size={14}/>}
                                                        sx={{
                                                            fontSize: 11,
                                                            textTransform: "none",
                                                            color: "text.secondary",
                                                            "&:hover": {bgcolor: "grey.100", color: "text.secondary"}
                                                        }}
                                                    >
                                                        View details
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {Object.keys(prompts).length > 0 && (
                                    <Box sx={{p: 2, borderTop: 1, borderColor: "divider", bgcolor: "grey.50"}}>
                                        <Typography fontSize={12} fontWeight={700} sx={{
                                            mb: 1.5,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.5,
                                            color: "text.secondary"
                                        }}>
                                            Available Prompts
                                        </Typography>

                                        <Stack gap={1}>
                                            {Object.entries(prompts).map(([name, text]) => {
                                                const isExpanded = !!expandedPrompts[name];
                                                return (
                                                    <Box
                                                        key={name}
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 1.5,
                                                            bgcolor: "background.paper",
                                                            border: 1,
                                                            borderColor: "divider",
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center"
                                                               justifyContent="space-between" sx={{mb: 0.75}}>
                                                            <Chip
                                                                label={name}
                                                                size="small"
                                                                sx={{fontSize: 11, fontWeight: 600}}
                                                            />
                                                            <Button
                                                                size="small"
                                                                onClick={() => togglePrompt(name)}
                                                                endIcon={
                                                                    <IconChevronRight
                                                                        size={14}
                                                                        style={{
                                                                            transition: "transform 0.2s",
                                                                            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)"
                                                                        }}
                                                                    />
                                                                }
                                                                sx={{
                                                                    fontSize: 11,
                                                                    textTransform: "none",
                                                                    color: "text.secondary",
                                                                    minWidth: 0,
                                                                    p: 0.5
                                                                }}
                                                            >
                                                                {isExpanded ? "Collapse" : "Expand"}
                                                            </Button>
                                                        </Stack>

                                                        <Typography
                                                            variant="body2"
                                                            fontSize={11.5}
                                                            color="text.secondary"
                                                            sx={{
                                                                whiteSpace: "pre-wrap",
                                                                ...(!isExpanded && {
                                                                    display: "-webkit-box",
                                                                    WebkitLineClamp: 3,
                                                                    WebkitBoxOrient: "vertical",
                                                                    overflow: "hidden",
                                                                })
                                                            }}
                                                        >
                                                            {text}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}
                            </Collapse>
                        </Box>
                    );
                })}
            </Stack>
            <ToolModal tool={selectedTool} open={!!selectedTool} onClose={() => setSelectedTool(null)}/>
        </Box>

    );
};

export default MCPDiscoveryPage;